import React, { useEffect, useRef, useState } from 'react';

import {
  CheckOutlined,
  DollarCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Drawer as DrawerAntD, Result, Skeleton, Table } from 'antd';
import moment from 'moment';
import { T } from 'react-translator-component';

import ReconciliationUploadModal from '../ReconciliationUploadModal';

import {
  Blank,
  Button,
  CardsContainer,
  Div,
  TitleWrapper,
  Title,
} from './styled';
import {
  DataType,
  TabType,
  mascaraMoeda,
  useFinancialTable,
} from './useFinancialTable';

import { useTheme } from '../../../hooks/useTheme';
import { COLORS } from '../../../utils/theme/colors';
import ChargerIcon from '../../atoms/icons/ChargerIcon';
import FinancialCard from '../../molecules/FinancialCard';
import FinancialDrawerDetails from '../../molecules/FinancialDrawerDetails';
import FinancialFooter from '../../molecules/FinancialFooter';
import FinancialHeader from '../../molecules/FinancialHeader';
import { Pagination } from '../../molecules/Pagination/Pagination';

const Drawer: any = DrawerAntD;

interface FinancialTableProps {
  tab: TabType;
}

interface CardsDataType {
  key: string;
  label: string;
  icon: React.JSX.Element;
  value?: string;
}

export interface OrderByType {
  label: string;
  value: OrderByTypeEnum;
}

export enum OrderByTypeEnum {
  FINANCIAL_MOVEMENT = 'FINANCIAL_MOVEMENT',
  CHRONOLOGICAL_ORDER = 'CHRONOLOGICAL_ORDER',
}

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 10;

const ALL_MOVEMENT_CARD_DATAS: CardsDataType[] = [
  {
    key: 'totalTransactions',
    label: 'Qtd. de movimentações',
    icon: <FileTextOutlined style={{ color: COLORS.primary }} />,
  },
  {
    key: 'countDeposits',
    label: 'Qtd. de depósitos',
    icon: <FileTextOutlined style={{ color: COLORS.primary }} />,
  },
  {
    key: 'countRecharges',
    label: 'Qtd. de recargas',
    icon: <FileTextOutlined style={{ color: COLORS.primary }} />,
  },
  {
    key: 'totalTransaction',
    label: 'Recargas pagas',
    icon: <CheckOutlined style={{ color: COLORS.primary }} />,
  },
  {
    key: 'totalPendingTransactions',
    label: 'Recargas pendentes',
    icon: <ClockCircleOutlined style={{ color: COLORS.primary }} />,
  },
  {
    key: 'totalUser',
    label: 'Depósito Bruto',
    icon: <DollarOutlined style={{ color: COLORS.primary }} />,
  },
];

const FinancialTable = ({ tab }: FinancialTableProps) => {
  const { theme } = useTheme();
  const isLeroyMerlin = theme === 'leroy_merlin';

  const user = JSON.parse(localStorage.getItem('auth-user'));
  const shouldShowBeneficiaryAccounts =
    isLeroyMerlin ||
    user?.permissions.some(
      (permission) =>
        permission.role_id === 'DASH_SHOW_BENEFICIARY_ACCOUNTS' &&
        !!permission.status,
    );

  const [financialFooterHeight, setFinancialFooterHeight] = useState(0);
  const [rowsData, setRowsData] = useState<DataType[]>([]);
  const [transactionPkSearch, setTransactionPkSearch] = useState('');
  const [reconciliationModalOpen, setReconciliationModalOpen] = useState(false);

  const [filterDate, setFilterDate] = useState<any>({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });
  const [orderBy, setOrderBy] = useState<OrderByType>({
    label: T('Financial movement'),
    value: OrderByTypeEnum.FINANCIAL_MOVEMENT,
  });

  const {
    openDrawerProps,
    financialInfoTransactions,
    financialInfoTransactionsSummary,
    setOpenDrawerProps,
    generateColumns,
    generateDataSource,
    tryToGetTransactions,
    tryToGetFinancialInfoSummary,
    downloadTransactions,
    handleStatus,
    tenants,
    chargeboxes,
  } = useFinancialTable();

  const dataSource = generateDataSource(tab);

  const onClose = () => {
    setOpenDrawerProps((prevState) => ({ ...prevState, visible: false }));
  };

  const handleDrawerVisibleChange = (visible) => {
    if (!visible && openDrawerProps.data !== null) {
      setOpenDrawerProps((prevState) => ({ ...prevState, data: null }));
    }
  };

  const handleFilter = (shouldResetPage) => {
    financialInfoTransactions.request(
      tab,
      shouldResetPage
        ? INITIAL_PAGE
        : financialInfoTransactions?.pagination?.page,
      shouldResetPage
        ? INITIAL_LIMIT
        : financialInfoTransactions?.pagination?.pageSize,
      filterDate.startDate,
      filterDate.endDate,
      transactionPkSearch,
      orderBy.value,
      filterDate.tenantId,
      filterDate.chargeBoxId,
      filterDate.paymentStatus,
    );

    financialInfoTransactionsSummary.request(
      shouldResetPage
        ? INITIAL_PAGE
        : financialInfoTransactions?.pagination?.page,
      shouldResetPage
        ? INITIAL_LIMIT
        : financialInfoTransactions?.pagination?.pageSize,
      filterDate.startDate,
      filterDate.endDate,
      transactionPkSearch,
      orderBy.value,
      filterDate.tenantId,
      filterDate.chargeBoxId,
      filterDate.paymentStatus,
    );

    setRowsData([]);
  };

  const rowSelection = {
    hideSelectAll: false,
    onSelect(select) {
      setRowsData((prevState) => {
        const itemIndex = prevState.findIndex(
          (item) => item.key === select.key && item.isSelected,
        );

        if (itemIndex > -1) {
          return prevState.filter((item) => item.key !== select.key);
        }

        return [
          ...prevState,
          {
            ...select,
            isSelected: true,
          },
        ];
      });
    },

    onSelectAll(selected) {
      if (selected) {
        const allRows = dataSource.map((item) => ({
          ...item,
          isSelected: true,
        })); //
        setRowsData(allRows);
      } else {
        setRowsData([]);
      }
    },
  };

  const getSummaryCardsData = (): CardsDataType[] => {
    if (!financialInfoTransactionsSummary.data) {
      return [];
    }

    return ALL_MOVEMENT_CARD_DATAS.map((allMovementCardData) => ({
      ...allMovementCardData,
      value:
        allMovementCardData.key !== 'totalTransactions' &&
        allMovementCardData.key !== 'countDeposits' &&
        allMovementCardData.key !== 'countRecharges'
          ? mascaraMoeda(
              (
                financialInfoTransactionsSummary?.data[
                  allMovementCardData.key
                ] || 0
              ).toFixed(2),
            )
          : financialInfoTransactions?.pagination.total || 0,
    }));
  };

  const tryToGetTransactionsRef = useRef(tryToGetTransactions);
  const tryToGetFinancialInfoSummaryRef = useRef(tryToGetFinancialInfoSummary);
  const tenantsRequestRef = useRef(tenants.request);
  const chargeboxesRequestRef = useRef(chargeboxes.request);

  useEffect(() => {
    const fetchData = async () => {
      await tryToGetTransactionsRef.current(tab);
      await tryToGetFinancialInfoSummaryRef.current();
      await tenantsRequestRef.current();
      await chargeboxesRequestRef.current();
    };

    fetchData();
  }, [tab]);

  const summaryData = getSummaryCardsData();

  const filteredSummaryCardsData =
    summaryData.length > 0
      ? summaryData.filter((cardData) => {
          console.log(cardData.key);
          switch (tab) {
            case TabType.DEPOSITS:
              return (
                cardData.key === 'countDeposits' || cardData.key === 'totalUser'
              );
            case TabType.RECHARGES:
              return (
                cardData.key === 'countRecharges' ||
                cardData.key === 'totalTransaction' ||
                cardData.key === 'totalPendingTransactions'
              );

            default:
              return (
                cardData.key !== 'countRecharges' &&
                cardData.key !== 'countDeposits'
              );
          }
        })
      : [];

  return (
    <Div financialFooterHeight={financialFooterHeight}>
      <ReconciliationUploadModal
        visible={reconciliationModalOpen}
        onClose={() => setReconciliationModalOpen(false)}
        onDone={() => handleFilter(false)}
      />

      <FinancialHeader
        filterDate={filterDate}
        orderBy={orderBy}
        onFilter={handleFilter}
        setOrderBy={setOrderBy}
        setFilterDate={setFilterDate}
        transactionPkSearch={transactionPkSearch}
        setTransactionPkSearch={setTransactionPkSearch}
        downloadTransactions={downloadTransactions}
        tab={tab}
        tenants={tenants.data}
        chargeboxes={chargeboxes.data}
        shouldShowBeneficiaryAccounts={shouldShowBeneficiaryAccounts}
        onReconcileClick={() => setReconciliationModalOpen(true)}
      />

      {tab === TabType.ALLMOVEMENTS &&
        financialInfoTransactionsSummary.loading && (
          <CardsContainer>
            <Skeleton
              active
              paragraph={{ rows: 2, width: '100%' }}
              title={false}
            />
          </CardsContainer>
        )}

      {!financialInfoTransactionsSummary.loading &&
        !financialInfoTransactionsSummary.error &&
        filteredSummaryCardsData.length > 0 && (
          <CardsContainer>
            {filteredSummaryCardsData.map((financialCardData, index) => (
              <FinancialCard
                key={index}
                icon={financialCardData.icon}
                label={financialCardData.label}
                value={financialCardData.value}
              />
            ))}
          </CardsContainer>
        )}

      {!!financialInfoTransactions.loading && (
        <Skeleton
          active
          paragraph={{ rows: 18, width: '100%' }}
          title={false}
        />
      )}

      {!!financialInfoTransactions.error && (
        <Blank>
          <Result
            status='warning'
            title='Não foi possível listar as movimentações financeiras!'
            extra={[
              <Button
                type='primary'
                key='reload'
                onClick={() => {
                  financialInfoTransactions.request();
                  financialInfoTransactionsSummary.request();
                }}
              >
                Tentar novamente
              </Button>,
            ]}
          />
        </Blank>
      )}

      {!financialInfoTransactions.loading &&
        !financialInfoTransactions.error && (
          <>
            <Table
              locale={{ emptyText: 'Não há movimentações financeiras' }}
              rowClassName='custom-table-row'
              rowSelection={{
                selectedRowKeys: rowsData.map((row) => row.key),
                type: 'checkbox',
                ...rowSelection,
              }}
              pagination={false}
              columns={generateColumns(tab)}
              dataSource={dataSource}
            />
            <Pagination
              currentPage={financialInfoTransactions?.pagination?.page}
              pageSize={financialInfoTransactions?.pagination?.pageSize}
              totalCount={financialInfoTransactions?.pagination?.total}
              onPageChange={(page: number, pageSize: number | undefined) => {
                financialInfoTransactions.request(
                  tab,
                  page,
                  pageSize,
                  filterDate.startDate,
                  filterDate.endDate,
                  transactionPkSearch,
                  orderBy.value,
                  filterDate.tenantId,
                  filterDate.chargeBoxId,
                  filterDate.paymentStatus,
                );
              }}
            />
          </>
        )}

      <Drawer
        width={480}
        title={
          <TitleWrapper>
            {openDrawerProps.data?.targetType === 'Recarga' ? (
              <ChargerIcon
                style={{ marginTop: 4 }}
                fill='#2B2B2B'
                height={20}
                width={20}
              />
            ) : (
              <DollarCircleOutlined
                style={{ color: '#2B2B2B', fontSize: 20 }}
              />
            )}

            <Title>{openDrawerProps.data?.targetType}</Title>
          </TitleWrapper>
        }
        onClose={onClose}
        afterVisibleChange={handleDrawerVisibleChange}
        visible={openDrawerProps.visible}
      >
        <FinancialDrawerDetails
          data={openDrawerProps.data}
          handleStatus={handleStatus}
          handleUpdatePage={handleFilter}
          setOpenDrawerProps={setOpenDrawerProps}
        />
      </Drawer>

      {rowsData.length > 0 && (
        <FinancialFooter
          rowsData={rowsData}
          setRowsData={setRowsData}
          setFinancialFooterHeight={setFinancialFooterHeight}
        />
      )}
    </Div>
  );
};

export default FinancialTable;
